import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, extractError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import FrameworkWheel from "../components/FrameworkWheel";

const ROLE_LABELS = {
    platform_admin: "Platform Admin",
    reviewer: "Reviewer",
    org_leader: "Organization Leader",
    coach: "Instructional Coach",
    teacher: "Teacher",
};

export default function AcceptInvitationPage() {
    const { token } = useParams();
    const [invitation, setInvitation] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [loadingInv, setLoadingInv] = useState(true);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [fullName, setFullName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    const { acceptInvitation } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadingInv(true);
            try {
                const { data } = await api.get(`/auth/invitation/${token}`);
                if (!cancelled) setInvitation(data);
            } catch (e) {
                if (!cancelled) setLoadError(extractError(e, "Invitation not found or expired."));
            } finally {
                if (!cancelled) setLoadingInv(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);
        if (password.length < 8) {
            setFormError("Password must be at least 8 characters.");
            return;
        }
        if (password !== confirm) {
            setFormError("Passwords do not match.");
            return;
        }
        setSubmitting(true);
        const res = await acceptInvitation(token, password, fullName.trim());
        setSubmitting(false);
        if (res.ok) {
            toast.success("Account activated. Welcome to ESSS Coherence.");
            navigate("/app/dashboard", { replace: true });
        } else {
            setFormError(res.error);
        }
    };

    return (
        <div
            className="min-h-screen relative overflow-hidden bg-[#D6E6F2] text-[#0E1B2C] flex items-center justify-center px-6 py-12"
            data-testid="accept-invitation-page"
        >
            <div
                aria-hidden
                className="pointer-events-none absolute -bottom-40 -right-40 opacity-40"
            >
                <FrameworkWheel size={520} />
            </div>
            <div
                aria-hidden
                className="pointer-events-none absolute top-0 left-0 w-[440px] h-[440px] rounded-full bg-[#8AC4FF]/30 blur-3xl"
            />

            <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="relative w-full max-w-md z-10"
            >
                <div className="mb-8">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-3 text-[#0B2545] font-serif text-lg"
                        data-testid="invitation-brand-link"
                    >
                        <img
                            src="/esss-logo.png?v=3"
                            alt="ESSS"
                            className="h-10 w-10 rounded-md object-contain bg-white"
                            data-testid="invitation-logo"
                        />
                        <span className="font-semibold">ESSS Coherence</span>
                    </Link>
                </div>

                <div className="rounded-2xl bg-white/95 backdrop-blur-md border border-white shadow-xl shadow-[#0B2545]/10 p-8">
                    {loadingInv ? (
                        <div className="flex items-center gap-3 text-[#0B2545]" data-testid="invitation-loading">
                            <Loader2 className="animate-spin" size={18} />
                            Loading invitation…
                        </div>
                    ) : loadError ? (
                        <div className="space-y-4" data-testid="invitation-error">
                            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#B8641F]">
                                Invitation problem
                            </div>
                            <h1 className="font-serif text-2xl font-semibold text-[#0B2545]">
                                We couldn't load this invitation.
                            </h1>
                            <p className="text-sm text-[#0E1B2C]/70">{loadError}</p>
                            <Link to="/login">
                                <Button className="rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white">
                                    Back to sign in
                                </Button>
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">
                                Accept invitation
                            </div>
                            <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-[#0B2545] leading-tight mb-2">
                                You've been invited as{" "}
                                <span className="text-[#1E56A0]">
                                    {ROLE_LABELS[invitation.role] || invitation.role}
                                </span>
                                {invitation.organization && (
                                    <>
                                        {" "}
                                        to <span className="text-[#1E56A0]">{invitation.organization.name}</span>
                                    </>
                                )}
                                .
                            </h1>
                            <p
                                className="text-[#0E1B2C]/70 mb-2 text-sm"
                                data-testid="invitation-email-label"
                            >
                                Invited email: <strong className="text-[#0B2545]">{invitation.email}</strong>
                            </p>
                            <p className="text-[#0E1B2C]/70 mb-6 text-sm leading-relaxed">
                                Set your full name and a password to activate your account.
                            </p>

                            <form onSubmit={handleSubmit} className="space-y-4" data-testid="accept-form">
                                <div className="space-y-2">
                                    <Label className="text-[#0B2545]">Email</Label>
                                    <Input
                                        value={invitation.email}
                                        disabled
                                        className="h-11 bg-[#D6E6F2]/50 border-[#D6E6F2] cursor-not-allowed"
                                        data-testid="accept-email"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="accept-name" className="text-[#0B2545]">
                                        Full name
                                    </Label>
                                    <Input
                                        id="accept-name"
                                        required
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        placeholder="Rita Ortiz"
                                        className="h-11 bg-white border-[#D6E6F2] focus-visible:ring-[#1E56A0]"
                                        data-testid="accept-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="accept-password" className="text-[#0B2545]">
                                        New password
                                    </Label>
                                    <Input
                                        id="accept-password"
                                        type="password"
                                        required
                                        minLength={8}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="At least 8 characters"
                                        className="h-11 bg-white border-[#D6E6F2] focus-visible:ring-[#1E56A0]"
                                        data-testid="accept-password-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="accept-confirm" className="text-[#0B2545]">
                                        Confirm password
                                    </Label>
                                    <Input
                                        id="accept-confirm"
                                        type="password"
                                        required
                                        minLength={8}
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        className="h-11 bg-white border-[#D6E6F2] focus-visible:ring-[#1E56A0]"
                                        data-testid="accept-confirm-input"
                                    />
                                </div>
                                {formError && (
                                    <div
                                        className="text-sm rounded-md bg-red-50 border border-red-200 text-red-700 px-3 py-2"
                                        data-testid="accept-error"
                                    >
                                        {formError}
                                    </div>
                                )}
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full h-11 rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white font-semibold text-sm"
                                    data-testid="accept-submit-btn"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="animate-spin" size={16} />
                                            Activating…
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle2 size={16} />
                                            Activate account
                                        </>
                                    )}
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
